import { apiPost, encodePost, type PostValue } from '../api/client'

/**
 * Maparea formularelor CI3 pe POST-ul v4. Kit Install / Update / Uninstall
 * raman pe capetele de kit; pachetul programat, Install package, Stop si
 * Update All scriu aici.
 */

export function fieldsOf(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {}
  const data = new FormData(form)
  data.forEach((value, key) => {
    if (typeof value === 'string') out[key] = value
  })
  return out
}

export function multiFields(form: HTMLFormElement, name: string): string[] {
  return [...form.querySelectorAll<HTMLInputElement | HTMLOptionElement>(`[name="${name}"], [name="${name}[]"]`)]
    .flatMap((el) => {
      if (el instanceof HTMLSelectElement) {
        return [...el.selectedOptions].map((o) => o.value)
      }
      if (el instanceof HTMLOptionElement) return el.selected ? [el.value] : []
      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox' || el.type === 'radio') return el.checked ? [el.value] : []
        return [el.value]
      }
      return []
    })
    .filter((v) => v !== '')
}

/** Data + ora se lipesc aici, nu pe server, ca in scheduleInstall.php. */
export function joinDateTime(date: string, time: string): string {
  const day = date.trim()
  if (!day) return ''
  return `${day} ${time.trim() || '00:00'}`
}

export function schedulePayload(form: HTMLFormElement): Record<string, PostValue> {
  const fields = fieldsOf(form)
  const devices = multiFields(form, 'devices')
  const repeat = form.querySelector<HTMLInputElement>('#siRepeat')?.checked ?? false
  const kind = form.querySelector<HTMLSelectElement>('#siRecurrenceKind')?.value ?? '1'
  const recurrenceDay = kind === '3'
    ? form.querySelector<HTMLSelectElement>('#siDayOfMonth')?.value ?? ''
    : form.querySelector<HTMLSelectElement>('#siDayOfWeek')?.value ?? ''

  return {
    company_id: fields.company_id ?? '',
    package_id: fields.package_id ?? '',
    devices,
    scheduled_at: joinDateTime(fields.scheduled_date ?? '', fields.scheduled_time ?? ''),
    deadline_at: joinDateTime(fields.deadline_date ?? '', fields.deadline_time ?? ''),
    recurrence: repeat ? kind : '0',
    recurrence_day: recurrenceDay,
    recurrence_time: fields.recurrence_time ?? '',
    active_days: fields.active_days ?? '7',
  }
}

export function writePostTarget(form: HTMLFormElement): { path: string; success: string; confirm?: string } | null {
  const action = form.getAttribute('action') ?? ''
  if (form.id === 'scheduleInstallForm' || action.includes('saveScheduledInstall')) {
    return { path: '/softsee/saveScheduledInstall', success: 'Scheduled' }
  }
  if (form.classList.contains('installKit') && action.includes('installPackage')) {
    return {
      path: '/softsee/installPackage',
      success: 'Install instruction has been sent.',
      confirm: 'Are you sure?',
    }
  }
  if (form.classList.contains('updateAllFromKit') || action.includes('updateAllFromKit')) {
    return { path: '/softsee/updateAllFromKit', success: 'All devices have been updated.', confirm: 'Are you sure?' }
  }
  if (form.classList.contains('stopScheduledInstall') || action.includes('stopScheduledInstall')) {
    return { path: '/softsee/stopScheduledInstall', success: 'Stopped.' }
  }
  if (form.classList.contains('updateKit')) {
    const device = Number(fieldsOf(form).device || 0)
    return {
      path: `/devices/${device}/softsee-updates/kit`,
      success: 'Update command has been sent.',
      confirm: 'Are you sure?',
    }
  }
  if (form.classList.contains('uninstallApplicationForm')) {
    const device = Number(fieldsOf(form).device_id || 0)
    return {
      path: `/devices/${device}/softsee-uninstall/application`,
      success: 'Uninstall command has been sent.',
      confirm: 'Are you sure?',
    }
  }
  return null
}

export async function postWriteForm(form: HTMLFormElement): Promise<string> {
  const target = writePostTarget(form)
  if (!target) throw new Error('Something went wrong.')
  const fields = form.id === 'scheduleInstallForm' ? schedulePayload(form) : fieldsFrom(form)
  const body = await apiPost<{ status?: string; message?: string }>(target.path, fields)
  return body.message || target.success
}

function fieldsFrom(form: HTMLFormElement): Record<string, PostValue> {
  if (form.classList.contains('updateAllFromKit')) {
    const kitId = fieldsOf(form).kitId ?? ''
    const kitType = fieldsOf(form).kitType ?? ''
    const devices = [...form.querySelectorAll<HTMLInputElement>('input[name^="devices["]')].reduce<Array<{ deviceId: string }>>(
      (rows, input) => {
        const match = input.name.match(/^devices\[(\d+)]\[deviceId]$/)
        if (match) rows[Number(match[1])] = { deviceId: input.value }
        return rows
      },
      [],
    )
    return { kitId, kitType, devices: devices.filter(Boolean) }
  }
  return fieldsOf(form)
}

export function encodeSchedule(form: HTMLFormElement): string {
  return encodePost(schedulePayload(form)).toString()
}
